import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import visitService from '../../services/visitService';
import './VisitEmailAction.css';

/**
 * Page for email Approve/Reject links: /visit/:id/approve|reject and /followup/:id/approve|reject.
 * No login or password: the signed link token authorizes the action. Optional remark only.
 */
export default function VisitEmailAction({ action = 'approve', flow = 'visit' }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const isApprove = action === 'approve';
  const isFollowUp = String(flow || 'visit').toLowerCase() === 'followup';

  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [submitError, setSubmitError] = useState('');

  const title = isFollowUp
    ? (isApprove ? 'Approve follow-up letter' : 'Reject follow-up letter')
    : (isApprove ? 'Approve visit' : 'Reject visit');

  const primaryLabel = isFollowUp
    ? (isApprove ? 'Approve follow-up letter' : 'Reject follow-up letter')
    : (isApprove ? 'Approve visit' : 'Reject visit');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitError('');
    setLoading(true);
    try {
      const remarkTrim = remark.trim() || undefined;
      if (isFollowUp) {
        if (isApprove) {
          await visitService.followUpConfirmByToken(id, token, remarkTrim);
          setSuccessMessage('Follow-up letter approved successfully.');
        } else {
          await visitService.followUpRejectByToken(id, token, remarkTrim);
          setSuccessMessage('Follow-up letter rejected successfully.');
        }
      } else if (isApprove) {
        await visitService.confirmByToken(id, token, remarkTrim);
        setSuccessMessage('Visit approved successfully.');
      } else {
        await visitService.cancelByToken(id, token, remarkTrim);
        setSuccessMessage('Visit rejected successfully.');
      }
      setRemark('');
      setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Request failed. The link may be invalid, expired, or already used.';
      setSubmitError(msg);
    } finally {
      setLoading(false);
    }
  };

  const goDashboard = () => navigate('/dashboard', { replace: true });

  if (!token) {
    return (
      <div className="visit-email-action-standalone content-area p-4">
        <div className="alert alert-danger">
          Invalid or missing link. The approval link may have expired or already been used.
        </div>
        <button type="button" className="btn btn-primary" onClick={goDashboard}>
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="visit-email-action-standalone content-area p-4">
      {successMessage && (
        <div className="alert alert-success visit-email-action-alert" role="alert">
          {successMessage} Redirecting to dashboard…
        </div>
      )}
      {!successMessage && (
        <div className="visit-email-action-card">
          <h1 className="visit-email-action-title">{title}</h1>
          <p className="visit-email-action-intro text-muted">
            Use the button below to complete this action. Your email link is your authorization; you do not need to log in or enter a password.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="visit-email-remark">Remarks <span className="text-muted">(optional)</span></label>
              <textarea
                id="visit-email-remark"
                className="form-control"
                rows={3}
                placeholder="Optional note for the study team"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                disabled={loading}
              />
            </div>
            {submitError && (
              <div className="alert alert-danger visit-email-action-alert" role="alert">
                {submitError}
              </div>
            )}
            <div className="visit-email-action-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Submitting…' : primaryLabel}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={goDashboard} disabled={loading}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
